import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiContentDuplicate } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { DocumentArchiveAction } from '../archive/DocumentArchiveAction'
import { useCan, type PermissionBearingEntity } from '../../contexts/PermissionsContext'
import type { DocumentDuplicateRef } from '../../api/schema'

interface DocumentDuplicatesBannerProps {
    org: string
    docUid: string
    /** This document's own creation date, to tell whether it is the oldest copy. */
    createdAt?: string | null
    /** This document's archive date: an archived copy offers no archive action. */
    archivedAt?: string | null
    /** The byte-identical twins the document read lists, oldest first. */
    duplicates?: DocumentDuplicateRef[] | null
    /** The document itself, for its per-row `permissions` array. */
    document?: PermissionBearingEntity | null
    formatDate: (d: string | undefined) => string
}

/**
 * isOldestCopy says whether this document is the copy to keep: no live,
 * non-archived twin was created before it.
 */
export function isOldestCopy(createdAt: string | null | undefined, duplicates: DocumentDuplicateRef[]): boolean {
    if (!createdAt) return false
    const own = new Date(createdAt).getTime()
    return !duplicates.some((d) => !d.archived_at && new Date(d.created_at).getTime() < own)
}

/**
 * Warning shown on a document whose file is byte-identical to other live
 * documents of the organization (spec 2026-11-03-04). One link per twin,
 * oldest first; on a copy that is not the oldest, and when the caller can
 * write it, an "Archive this duplicate" action (the existing, reversible
 * archive — no merge, no deletion).
 */
export function DocumentDuplicatesBanner({
    org,
    docUid,
    createdAt,
    archivedAt,
    duplicates,
    document,
    formatDate,
}: DocumentDuplicatesBannerProps) {
    const { t } = useTranslation('documents')
    const canWrite = useCan('documents.write', document)

    if (!duplicates?.length) return null

    const oldest = isOldestCopy(createdAt, duplicates)
    const title = oldest
        ? t('duplicates.also_exists', { count: duplicates.length })
        : t('duplicates.is_duplicate_of')

    return (
        <div
            className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-sm space-y-2"
            data-testid="document-duplicates-banner"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 text-warning">
                    <AppIcon path={mdiContentDuplicate} size="sm" className="shrink-0" />
                    <span data-testid="document-duplicates-title">{title}</span>
                </div>
                {!oldest && canWrite && !archivedAt && (
                    <DocumentArchiveAction
                        org={org}
                        docUid={docUid}
                        archivedAt={archivedAt}
                        label={t('duplicates.archive_this')}
                        testId="document-duplicate-archive-button"
                    />
                )}
            </div>
            <ul className="space-y-1 pl-6">
                {duplicates.map((twin) => (
                    <li key={twin.uid} className="flex flex-wrap items-center gap-x-2" data-testid="document-duplicate-item">
                        <Link
                            to="/$org/documents/$docUid"
                            params={{ org, docUid: twin.uid }}
                            className="text-primary hover:underline"
                            data-testid="document-duplicate-link"
                        >
                            {twin.name || twin.uid}
                        </Link>
                        {twin.asset_name && <span className="text-muted-foreground">· {twin.asset_name}</span>}
                        <span className="text-muted-foreground">· {t('duplicates.created_on', { date: formatDate(twin.created_at) })}</span>
                        {twin.archived_at && (
                            <span
                                className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                                data-testid="document-duplicate-archived"
                            >
                                {t('duplicates.archived')}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}
